import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import AuthLayout from "../../components/layout/AuthLayout";
import { useCourseModule } from "./context/CourseModuleContext";
import { request } from "../../hooks/api/fetch";
import { IoIosArrowRoundBack } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { Paper, Button, Typography, Box, Stack, Skeleton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, ToggleButton, ToggleButtonGroup, FormControl, Input, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import ProgressBar from "./components/ProgressBar";
import CourseImage from "../../assets/images/Frame 1000003420.png";
import Modules from "./components/Modules";
import Assessment from "./components/Assessment";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";

// Custom Star Rating Component
const StarRating = ({ rating, onRatingChange, size = 30 }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <Box sx={{ display: "flex", justifyContent: "center", gap: "4px" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box
          key={star}
          sx={{
            cursor: "pointer",
            color: star <= (hoverRating || rating) ? "#f1d045" : "#e4e5e9",
            fontSize: `${size}px`,
            transition: "color 0.2s",
            "&:hover": {
              color: star <= rating ? "#f1b345" : "#f1d045",
            },
          }}
          onClick={() => onRatingChange(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
        >
          ★
        </Box>
      ))}
    </Box>
  );
};

// Feedback Modal Component
function FeedbackModal({ open, setOpen, courseId, courseName }) {
  const navigate = useNavigate();
  const [surveyAnswer, setSurveyAnswer] = useState({
    rating: 0,
    difficultyLevel: "",
    otherFeedback: "",
  });
  
  const handleRating = (rating) => {
    setSurveyAnswer((prev) => ({ ...prev, rating }));
  };
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ccAuth = JSON.parse(sessionStorage.getItem("ccAuth") || "{}");
  const userId = ccAuth?.id;

  const handleClose = () => {
    setOpen(false);
  };
  
  // Function to submit feedback
  const submitFeedback = async (feedbackData) => {
    try {
      // Replace with your actual API endpoint for course feedback
      const response = await request.post('/trivia/submit-feedback', {
        user_id: userId,
        quiz_id: courseId,
        type: "course",
        feedbacks: [
          {
            question: "How many star rating",
            answer: feedbackData.rating,
          },
          {
            question: "Difficulty level",
            answer: feedbackData.difficultyLevel,
          },
          {
            question: "Other Feedbacks",
            answer: feedbackData.otherFeedback,
          },
        ],
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit feedback');
    }
  };
  
  // Use mutation for feedback submission
  const feedbackMutation = useMutation(submitFeedback, {
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      toast.success("Thank you for your feedback!", {
        position: toast.POSITION.TOP_CENTER,
      });
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_CENTER,
      });
      console.error("Feedback submission error:", error);
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });
  
  function handleSubmitFeedback() {
    if (!surveyAnswer.rating || surveyAnswer.rating === 0) {
      toast.warning("Please select a rating", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }
    
    feedbackMutation.mutate(surveyAnswer);
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle align="center" variant="h5">
        We Would Love To Hear From You
      </DialogTitle>
      <DialogContent>
        <DialogContentText align="center">
          We would like your feedback to help us improve your experience with the course: {courseName}
        </DialogContentText>
        
        <Box sx={{ padding: "15px", backgroundColor: "#F8F8F8", marginTop: "20px", borderRadius: "10px" }}>
          <Box>
            <Typography fontWeight="500" gutterBottom>
              1. How would you rate your overall experience with this course?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <StarRating 
                rating={surveyAnswer.rating} 
                onRatingChange={handleRating}
                size={32}
              />
            </Box>
            <Typography variant="body2" color="textSecondary" align="center" mt={1}>
              {surveyAnswer.rating > 0 ? `You rated ${surveyAnswer.rating} star${surveyAnswer.rating !== 1 ? 's' : ''}` : 'Click a star to rate'}
            </Typography>
          </Box>
          
          <Box my={2.5}>
            <Typography fontWeight="500" gutterBottom>
              2. What is the difficulty level of the course content?
            </Typography>
            <Box mt={1}>
              <FormControl fullWidth>
                <RadioGroup
                  aria-labelledby="difficulty-level-label"
                  name="difficulty-level"
                  row
                  sx={{ justifyContent: "center", gap: 2 }}
                  onChange={(e) => {
                    setSurveyAnswer((prev) => ({
                      ...prev,
                      difficultyLevel: e.target.value,
                    }));
                  }}
                >
                  <FormControlLabel
                    value="difficult"
                    control={<Radio />}
                    label="Difficult"
                  />
                  <FormControlLabel
                    value="neutral"
                    control={<Radio />}
                    label="Neutral"
                  />
                  <FormControlLabel
                    value="easy"
                    control={<Radio />}
                    label="Easy"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
          
          <Box>
            <Typography fontWeight="500" gutterBottom>
              3. Do you have any suggestions or feedback on how we can enhance the course?
            </Typography>
            <Box mt={1}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="I would like if ______"
                variant="outlined"
                onChange={(e) => {
                  setSurveyAnswer((prev) => ({
                    ...prev,
                    otherFeedback: e.target.value,
                  }));
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", padding: "0 24px 20px" }}>
        <Button onClick={handleClose} variant="outlined" color="error" disabled={isSubmitting}>
          Skip
        </Button>
        <Button
          onClick={handleSubmitFeedback}
          variant="contained"
          color="primary"
          disabled={!surveyAnswer.rating || isSubmitting}
          sx={{ bgcolor: "#61A641", "&:hover": { bgcolor: "#4e8a34" } }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Main CourseModules Component
const CourseModules = () => {
  const navigate = useNavigate();
  const ccAuth = JSON.parse(sessionStorage.getItem("ccAuth") || "{}");
  const course = JSON.parse(sessionStorage.getItem("course"));
  const cohortParticipantId = sessionStorage.getItem("cohortParticipantId");
  const journeyCohortId = sessionStorage.getItem("journeyCohortId");
  const userId = ccAuth?.id;
  const courseId = course?.courseId;

  const { courseModules, setCourseModules } = useCourseModule();
  const [showFeedback, setShowFeedback] = useState(false);

  const completedModules =
    courseModules?.modules?.reduce(
      (acc, module) => acc + (module?.moduleCompletion ? 1 : 0),
      0
    ) || 0;

  const completionPercentage = (modules) => {
    const totalModules = modules?.length;
    const completedModules = modules?.reduce(
      (acc, module) => acc + (module?.moduleCompletion ? 1 : 0),
      0
    );
    return (completedModules / totalModules) * 100;
  };

  const percentage = completionPercentage(courseModules?.modules);

  // Check if all modules are completed
  useEffect(() => {
    if (courseModules?.modules && courseModules.modules.length > 0) {
      const allModulesCompleted = courseModules.modules.every(
        module => module.moduleCompletion === true
      );
      
      // Check if we haven't shown feedback for this course yet
      const feedbackShown = sessionStorage.getItem(`feedbackShown_${courseId}`);
      
      if (allModulesCompleted && !feedbackShown) {
        setShowFeedback(true);
      }
    }
  }, [courseModules, courseId]);

  // Fetch Course Modules
  const getAssignedCourseModule = async (
    userId,
    cohortParticipantId,
    journeyCohortId,
    courseId
  ) => {
    const { data } = await request.get(
      `/trivia/get-assigne-course-module/${userId}?cohortParticipantId=${cohortParticipantId}&journeyCohortId=${journeyCohortId}&courseId=${courseId}`
    );
    return data;
  };

  const {
    mutate,
    isLoading: courseModuleLoading,
    error: courseModuleError,
  } = useMutation(
    ({ userId, cohortParticipantId, journeyCohortId, courseId }) =>
      getAssignedCourseModule(
        userId,
        cohortParticipantId,
        journeyCohortId,
        courseId
      ),
    {
      onSuccess: (data) => {
        setCourseModules(data.data);
        console.log(data, "journey");
      },
      onError: (error) => {
        console.error(error);
      },
    }
  );

  // Trigger mutation on component mount
  useEffect(() => {
    if (userId && cohortParticipantId && journeyCohortId && courseId) {
      mutate({ userId, cohortParticipantId, journeyCohortId, courseId });
    }
  }, [userId, cohortParticipantId, journeyCohortId, courseId, mutate]);

  return (
    <AuthLayout data-testid="authLayout">
      <div className="py-6">
        <div>
          <Link
            onClick={() => navigate(-1)}
            className="flex px-5 py-2 bg-white mt-3 shadow border border-gray-100 items-center justify-start gap-1 max-w-fit"
          >
            <IoIosArrowRoundBack className="w-4" />
            <Typography
              variant="h3"
              fontSize={20}
              fontWeight={900}
              align="center"
            >
              Back
            </Typography>
          </Link>
        </div>
      </div>
      {courseModuleLoading ? (
        <div className="flex justify-center items-center h-screen">
            <ClipLoader className="bg-[#F07404]" />
        </div>
      ) : (
        <section className="bg-white p-7 border-t border-gray-100 shadow mt-4">
          <div className="w-full">
            <div className="flex w-full sm:flex-row flex-col justify-between items-center gap-3">
              <Typography variant="h3" fontSize={24} fontWeight={700}>
                {courseModules?.courseName}
              </Typography>
            </div>
            <section className="grid grid-cols-2 gap-4 mt-4 w-full">
              <Typography
                fontSize={12}
                fontWeight={500}
                className="flex text-gray-700 border border-gray-100 font-medium py-3 justify-center rounded-md bg-gray-100 items-center"
                as="span"
              >
                modules: &nbsp;
                {courseModules?.modules?.length}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                className="flex text-gray-700 border border-gray-100 font-medium py-3 justify-center rounded-md bg-gray-100 items-center"
                as="span"
              >
                completed: &nbsp;
                {completedModules}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                className="flex text-gray-700 border border-gray-100 font-medium py-3 justify-center rounded-md bg-gray-100 items-center"
                as="span"
              >
                assessments: &nbsp;
                {course?.numberOfAssessments}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                className="flex text-gray-700 border border-gray-100 font-medium py-3 justify-center rounded-md bg-gray-100 items-center"
                as="span"
              >
                completed: &nbsp;
                {course?.numberOfCompletedAssessments}
              </Typography>
            </section>
          </div>
          <section className="md:flex flex justify-between w-full mt-4 items-center">
            <div className="flex-1 flex items-center justify-start gap-2">
              <ProgressBar progress={percentage} />
              <Typography fontSize={12} fontWeight={500}>
                {Math.round(percentage)}%
              </Typography>
            </div>
          </section>
          <section className="w-full mt-4">
            <img
              src={CourseImage}
              alt="course"
              className="h-[250px] w-full"
            />
          </section>
          <section className="mt-5">
            <div className="">
              {courseModules?.modules?.map((module, index) => {
                const previousCourseCompleted =
                  index === 0 ||
                  courseModules?.modules[index - 1].moduleCompletion === true;
                const isLocked = !previousCourseCompleted;
                return (
                  <Modules
                    index={index + 1}
                    module={module}
                    key={module?.moduleId}
                    isLocked={isLocked}
                  />
                );
              })}
              {courseModules?.assessments?.map((module, index) => {
                const previousCourseCompleted =
                  index === 0 ||
                  courseModules.modules[index - 1].moduleCompletion === true;
                const isLocked = !previousCourseCompleted;
                return (
                  <Assessment
                    module={module}
                    index={index + 1}
                    key={module?.moduleId}
                    isLocked={percentage !== 100}
                  />
                );
              })}
            </div>
          </section>
        </section>
      )}
      {courseModuleError && (
        <p className="text-red-500">Error loading modules.</p>
      )}
      
      {/* Feedback Modal */}
      <FeedbackModal 
        open={showFeedback} 
        setOpen={setShowFeedback} 
        courseId={courseId}
        courseName={courseModules?.courseName}
      />
    </AuthLayout>
  );
};

export default CourseModules;